import React, { useState, useEffect } from 'react';
import { Loader2, Check, X, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '../services/api';
import { TaskStatus } from '../types/api';

const BulkLinkButton: React.FC = () => {
  const [taskId, setTaskId] = useState<number | null>(null);
  const [status, setStatus] = useState<TaskStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const handleRun = async () => {
    try {
      const response = await apiClient.runBulkLink();
      setTaskId(response.task_id);
      setStatus('running');
      setIsPolling(true);
    } catch (error) {
      console.error('Failed to start bulk link:', error);
      alert('Failed to start bulk link. Please try again.');
    }
  };

  // Poll task status
  useEffect(() => {
    if (!taskId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const taskStatus = await apiClient.getTaskStatus(taskId);
        setStatus(taskStatus.status);

        if (taskStatus.status === 'completed' || taskStatus.status === 'failed') {
          setIsPolling(false);
          setTimeout(() => {
            setTaskId(null);
            setStatus(null);
          }, 3000);
        }
      } catch (error) {
        console.error('Failed to poll task status:', error);
        // Reset fully so the button re-enables and the user can retry — leaving
        // status='running' would keep it disabled until a page reload.
        setIsPolling(false);
        setStatus(null);
        setTaskId(null);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [taskId, isPolling]);

  const getButtonText = () => {
    if (status === 'running') return 'Linking...';
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return 'Link All Deals';
  };

  const isDisabled = status === 'running' || status === 'completed' || status === 'failed';

  const getButtonVariant = () => {
    if (status === 'failed') return 'destructive' as const;
    return 'default' as const;
  };

  const getButtonClassName = () => {
    if (status === 'completed') return 'bg-green-600 hover:bg-green-700';
    return '';
  };

  return (
    <Button
      onClick={handleRun}
      disabled={isDisabled}
      variant={getButtonVariant()}
      className={getButtonClassName()}
    >
      <div className="flex items-center justify-center gap-2">
        {status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
        {status === 'completed' && <Check className="h-4 w-4" />}
        {status === 'failed' && <X className="h-4 w-4" />}
        {!status && <Link2 className="h-4 w-4" />}
        <span>{getButtonText()}</span>
      </div>
    </Button>
  );
};

export default BulkLinkButton;
